import moment from "moment"
import React from "react"
import { Button, Col, Row } from "react-bootstrap"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
import { useTranslation } from "react-i18next"
import { PickBasicModel } from "../../api/pick.api"
import { useAppDispatch } from "../../app/hooks"
import aramex from "../../assets/ShippingCompaniesIcons/aramex.svg"
import dhl from "../../assets/ShippingCompaniesIcons/dhl.svg"
import fedex from "../../assets/ShippingCompaniesIcons/fedex.svg"
import shopini from "../../assets/ShippingCompaniesIcons/shopini-economy.svg"
import thabit from "../../assets/ShippingCompaniesIcons/thabit.svg"
import ups from "../../assets/ShippingCompaniesIcons/ups.svg"
import usps from "../../assets/ShippingCompaniesIcons/usps.svg"
import { SearchBar } from "../../components/Tables/Columns"
// import { logoUrl } from "config/config";
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { pickParcel } from "../../store/pick.store"

function PickBasic({
    picksBasic,
    setButtonId,
    exportLinkLoading = false,
    handleExportReadyToPick = () => console.log()
}: {
    setButtonId?: Function
    picksBasic: PickBasicModel[]
    exportLinkLoading?: boolean
    handleExportReadyToPick?: Function
}) {
    const { t } = useTranslation()
    if (setButtonId) {
        setButtonId("#pickBasicExportButton")
    }

    const dispatch = useAppDispatch()

    const handleAction = (action: string, parcelUuid: string) => {
        switch (action) {
            case "Pick":
                return dispatch(pickParcel({ parcelUuid }))
            default:
                break
        }
    }

    const pickBasicParcelsColumns = [
        {
            dataField: "created_at",
            text: t("Date"),
            sort: true,
            formatter: (cell: number, row: Record<string, any>) => {
                if (cell) return moment(cell).format("DD MMM yyyy")
            }
        },
        {
            dataField: "uuid",
            text: t("Parcel ID"),
            style: {
                width: 230,
                color: "#1E88E5"
            }
        },
        {
            dataField: "is_new_awb_generated",
            text: t("Label Status"),
            style: {
                width: 400
            },
            formatter: (cell: boolean, row: Record<string, any>) => {
                let colorClass = ""
                let status = 1
                if (cell || (row?.shipping_company_id && parseInt(row?.shipping_company_id) !== 14)) {
                    colorClass = "active_status"
                    status = 2
                } else {
                    colorClass = "in-process"
                    status = 1
                }
                return (
                    <span className={`awb-status ${colorClass}`}>
                        {t(`${status === 1 ? "Generating" : "Generated"}`)}
                    </span>
                )
            }
        },
        {
            text: setButtonId ? t("Items / Parcels") : t("Items"),
            dataField: "ParcelItems.length",
            formatter: (cell: number, row: Record<string, any>) => {
                if (row?.parcel_type_id === 1) {
                    return (
                        <>
                            {cell} <strong className="mx-2">{t("items")}</strong>{" "}
                        </>
                    )
                } else {
                    return (
                        <>
                            {row?.Children?.length || 0} <strong className="mx-2">{t("parcels")}</strong>{" "}
                        </>
                    )
                }
            }
        },

        {
            dataField: "shipping_company_id",
            text: t("Shipping"),
            formatter: (cell: string | number) => {
                const className = setButtonId ? "" : "w-50"
                switch (cell) {
                    case 1:
                        return (
                            <img className={className} width="120" height="80" src={aramex} alt="aramex_icon" />
                        )
                    case 2:
                        return <img className={className} width="120" height="80" src={dhl} alt="dhl_icon" />
                    case 5:
                        return (
                            <img className={className} width="120" height="80" src={fedex} alt="fedex_icon" />
                        )
                    case 6:
                        return <img className={className} width="120" height="80" src={usps} alt="usps_icon" />
                    case 4:
                        return (
                            <img
                                className={className}
                                width="120"
                                height="80"
                                src={shopini}
                                alt="shopini_icon"
                            />
                        )
                    case 8:
                        return <img className={className} width="120" height="80" src={ups} alt="ups_icon" />
                    case 14:
                        return (
                            <img className={className} width="120" height="80" src={thabit} alt="thabit_icon" />
                        )
                    default:
                        break
                }
            }
        },
        {
            dataField: "",
            text: t("Actions"),
            formatter: (cell: number, row: Record<string, any>) => {
                // return <Dropdown className="actions-menu">
                // 	<Dropdown.Toggle variant="primary" id="dropdown-basic" size={"sm"} className="py-0">
                // 		{t("Actions")}
                // 	</Dropdown.Toggle>

                // 	<Dropdown.Menu>
                // 		{["Pick"].map(action =>
                // 			<Dropdown.Item className="py-0" onClick={() => handleAction(action, row?.uuid)}>
                // 				{t(action)}
                // 			</Dropdown.Item>
                // 		)}
                // 	</Dropdown.Menu>
                // </Dropdown>
                return <Button onClick={() => handleAction("Pick", row?.uuid)}>{t("Pick")}</Button>
            }
        }
    ]
    const options = {
        sizePerPage: 10,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className={` ${setButtonId ? "table-responsive" : "overflow-x-hidden"}`}>
            {pickBasicParcelsColumns && (
                <ToolkitProvider
                    keyField="id"
                    data={picksBasic}
                    columns={pickBasicParcelsColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "PickBasicTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search mx-5 justify-content-between">
                                {setButtonId && (
                                    <Col xs={5} sm={4} md={3} lg={6} className="search-width">
                                        <SearchBar {...props.searchProps} />
                                    </Col>
                                )}
                                <Col lg={6} className="d-flex justify-content-end">
                                    {/*<ExportCSVButton className="readyPickExport" {...props.csvProps}>
										Export
									</ExportCSVButton>*/}
                                    {setButtonId ? (
                                        exportLinkLoading ? (
                                            <div className="spinner-border text-primary" role="status"></div>
                                        ) : (
                                            <Button
                                                variant={"info text-white"}
                                                onClick={() => handleExportReadyToPick()}>
                                                {t("Export Ready to Pick")}
                                            </Button>
                                        )
                                    ) : null}
                                </Col>
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <BootstrapTable
                                        {...props.baseProps}
                                        wrapperClasses="table-wrapper my-3"
                                        headerClasses="header-class"
                                        bordered={false}
                                        noDataIndication={t("No_Record")}
                                        filter={setButtonId && filterFactory()}
                                        rowClasses="custom-table-row pick-basic"
                                        condensed={true}
                                        pagination={setButtonId && paginationFactory(options)}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default PickBasic
